/* screens/checkout/CheckoutFail.jsx — экран неудачной оплаты (S11).
   Опц. карточка деталей + причина. Состояния: checking / failed / no_payment.

   ВАЖНО: страница отказа НЕ является приговором. Робокасса присылает браузер
   на FailURL и в тех случаях, когда оплата на самом деле проходит — при
   SberPay/СБП возврат из банковского приложения выглядит как отмена, а
   подписанный ResultURL об успехе приходит секундами позже. Поэтому здесь
   тот же polling статуса, что и на CheckoutSuccess: успех — уводим на экран
   успеха, и до выяснения НЕ утверждаем, что деньги не списаны (клиент, увидев
   такое обещание при списанных деньгах, идёт платить второй раз).
   РОУТ ВНЕ RequireAuth (гард переехал в RequireAuthOrPayLink): счёт по именной
   ссылке вправе оплатить посторонний, у которого аккаунта нет, и шлюз возвращает
   его сюда. Гость опознаётся по `?pl=` и читает статус токен-скоупом.
   Источник: 12 §2.1, Сценарий 7. ХЕНДОФФ: → pages/checkout/CheckoutFail.jsx. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { PLAN_LABELS } from '../../lib/adapters/checkout.js';
import { subscriptionReal } from '../../lib/adapters/subscription.js';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';
import { CheckoutLayout } from './CheckoutLayout.jsx';
import { CheckoutRow } from './CheckoutRow.jsx';

  const { useState, useEffect, useRef } = React;
  const { useSearchParams, useNavigate } = RR;
  const { useFakeQuery } = hooks;

  // Столько ждём запоздавший ResultURL. Робокасса шлёт его почти сразу, но
  // ретраит с интервалом ~1 мин, поэтому окно шире, чем у CheckoutSuccess.
  const MAX_TICKS = 30;   // 30 × 2с = 60с
  const TICK_MS = 2000;

  function CheckoutFail() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const invoiceId = params.get('invoice_id');
    const ws = params.get('ws') || undefined;   // бизнес-ws счёта (не активного) → X-Workspace-Id для статуса
    // Токен именной ссылки, если оплата шла по ней (принёс редирект шлюза).
    const plToken = params.get('pl') || '';
    const { user } = useAuth();
    // Аккаунта нет — перед нами посторонний плательщик: сессионный
    // `/payments/<id>/` ответил бы ему 404, и экран навсегда завис бы на
    // «Проверяем оплату» вместо честного исхода.
    const guest = !!plToken && !user;
    const fetchStatus = React.useCallback(
      () => (guest
        ? subscriptionReal.getLinkPaymentStatus(plToken, invoiceId)
        : subscriptionReal.getPaymentStatus(invoiceId, ws)),
      [guest, plToken, invoiceId, ws],
    );
    // платёж может быть не найден — это допустимо (no_payment)
    const { data: payment, loading } =
      useFakeQuery('pay-fail-' + invoiceId + (ws ? ':' + ws : '') + (guest ? ':g' : ''),
        () => fetchStatus().catch(() => null), [fetchStatus]);

    // 'checking' — исход ещё не подтверждён (ждём возможный ResultURL);
    // 'failed' — отказ подтверждён статусом платежа; 'unknown' — платёж не найден.
    const [phase, setPhase] = useState('checking');
    const ticks = useRef(0);

    const goSuccess = () => navigate('/checkout/success?invoice_id=' + encodeURIComponent(invoiceId)
      + (ws ? '&ws=' + encodeURIComponent(ws) : '')
      // Токен уносим с собой: без него страница успеха выкинет гостя на вход.
      + (plToken ? '&pl=' + encodeURIComponent(plToken) : ''));

    // Исход считается решённым, только если статус терминальный И не помечен
    // бэкендом как предварительный (`is_provisional` — статус, поставленный
    // догадкой, которую настоящий ResultURL ещё может отменить). Именно в этом
    // состоянии страница и открывается после FailURL, поэтому «оплата не
    // прошла» на первом же кадре — тот самый ложный приговор.
    const settledAsFailed = (p) => !!p && !p.is_provisional
      && (p.status === 'failed' || p.status === 'cancelled');

    // первичная загрузка
    useEffect(() => {
      if (!invoiceId) { setPhase('unknown'); return; }
      if (loading) return;
      if (!payment) { setPhase('unknown'); return; }
      if (payment.status === 'succeeded') { goSuccess(); return; }
      if (settledAsFailed(payment)) setPhase('failed');
    }, [invoiceId, loading, payment]);

    // polling: платёж мог быть закрыт нашей догадкой, а деньги — прийти следом
    useEffect(() => {
      if (!invoiceId || phase !== 'checking') return;
      ticks.current = 0;
      const tick = () => {
        ticks.current += 1;
        fetchStatus().then((st) => {
          if (st && st.status === 'succeeded') { goSuccess(); }
          // отказ подтверждён бэкендом (метку догадки сняли) — ждать больше нечего
          else if (settledAsFailed(st)) { setPhase('failed'); }
          else if (ticks.current >= MAX_TICKS) { setPhase('failed'); }
        }).catch(() => { if (ticks.current >= MAX_TICKS) setPhase('failed'); });
      };
      const t = setInterval(tick, TICK_MS);
      const first = setTimeout(tick, 600); // быстрая первая проверка
      return () => { clearInterval(t); clearTimeout(first); };
    }, [invoiceId, ws, phase, fetchStatus]);

    const checking = phase === 'checking';
    const planLabel = (p) => p.plan_label || PLAN_LABELS[p.plan_slug] || p.plan_slug;

    return (
      <CheckoutLayout>
        <header className="checkout-head checkout-head--center">
          <div className={'checkout-icon ' + (checking ? 'checkout-icon--warn' : 'checkout-icon--bad')}>
            <Icon name={checking ? 'spinner' : 'x-circle'} size={30} />
          </div>
          <div className="t-eyebrow text-dim">Оплата подписки</div>
          <h1 className="t-h2">{checking ? 'Проверяем оплату' : 'Оплата не прошла'}</h1>
          <p className="t-body text-dim">
            {checking
              ? 'Платёжная система вернула вас без подтверждения. Проверяем, дошли ли деньги — это занимает до минуты. Не начинайте оплату заново, пока идёт проверка.'
              : 'Списание не выполнено. Можно повторить попытку — деньги не списаны.'}
          </p>
        </header>

        {loading && (
          <div className="checkout-state checkout-state--inline"><Icon name="spinner" size={22} /><span className="text-dim">Загружаем детали…</span></div>
        )}

        {!loading && payment && (
          <div className="checkout-details">
            <CheckoutRow label="Тариф" value={planLabel(payment)} />
            <CheckoutRow label="Сумма" value={fmt.money(payment.amount_rub)} mono tone="muted" />
            <CheckoutRow label="Счёт" value={payment.invoice_id} mono />
            {/* причину показываем только когда отказ подтверждён: пока идёт
                проверка, «пользователь отменил оплату» — это ещё догадка */}
            {!checking && payment.failure_reason && (
              <CheckoutRow label="Причина" value={payment.failure_reason} tone="danger" />
            )}
          </div>
        )}

        {/* Гостю ни «Тарифы», ни «На главную» не годятся: за ними RequireAuth,
            и обе увели бы его на /signin. Ему нужен ровно один путь — назад к
            счёту, откуда попытку оплаты можно повторить. */}
        <div className="checkout-actions">
          <Button variant="primary" className="full" disabled={checking}
                  onClick={() => navigate(guest
                    ? '/pay/' + encodeURIComponent(plToken)
                    : '/settings?tab=subscription')}>
            {checking ? 'Проверяем…' : (guest ? 'Вернуться к счёту' : 'Вернуться к тарифам')}
          </Button>
          {!guest && (
            <a className="link checkout-link" href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>На главную</a>
          )}
        </div>
      </CheckoutLayout>
    );
  }

export { CheckoutFail };
